import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AIChatItemModel, aiPanelAsk, AIPanel as AIPanelComp, ChatItemAudioRenderer, IAIChatItem, IAIChatReference, IAIPanelHandles, streamToAnswerItemWithMarkdown } from '@dauyan/ai-chat';
import { useMemoizedFn } from 'ahooks';
import { useParams } from 'react-router';
import global from '@/utils/global';
import { isTeacherMockContext } from '@/utils/homeworkSession';

import Bus from '@/utils/bus';
import AIContant from '@/utils/aiContant';
import { playAudio, stopAudio, focusNumberAI } from '@/utils/audioPlay';
import logger from '@/utils/logger/arms-config';
import { createWebsocketInstance } from '@/utils/network/web-socket';
import {toReference} from '@/utils/toReference';
import { logBDEvent } from '@/utils/logger/bd-tracker';
import { useModulePoints } from '@/hooks/useModulePoints';
import i18n from '@/locales/init'; 
import { saveListeningAIChatRecord, saveReadingAIChatRecord, fetchListeningAIHistory, fetchReadingAIHistory } from '@/api/ai';
import { updatePointsDelay } from '../Points/updatePoints';
import { ChatListBackground } from './ChatListBackground';
import { AITitle } from './AITitle';
import { remoteToLocal } from './remoteToLocal';
import { remoteToLocal as remoteToLocalRead} from './remoteToLocalRead';
import lessModule from './index.module.less';
import './ai-markdown.less';
import { AIChatInputHOC } from './AIChatInput';
// import { useTimer } from './useTimer';

export interface IAIPanelProps {
  paper_id?: number;
  course: 'l' | 'r';
}

const pointsMapp = {
  l: 10,
  r: 11,
};

export function AIPanel(props: IAIPanelProps) {
  if (isTeacherMockContext()) return null;
  return <StudentAIPanel {...props} />;
}

const StudentAIPanel = React.memo<IAIPanelProps>(({ paper_id, course }) => {

  const params = useParams();

  const recordId = Number(params.recordId);
  const panelRef = useRef<IAIPanelHandles | null>(null);


  // 超时日志
  // const handleAskingTimeout = useMemoizedFn(() => {
  //   logger.logCount('Websocket_TIMEOUT');
  // });
  // const [startTimer, stopTimer] = useTimer(handleAskingTimeout, 1000 * 8);

  const [topFinished, setTopFinished] = useState<boolean>(false);
  const [lastId, setLastId] = useState<number>(0);
  const [fetching, setFetching] = useState<boolean>(false);

  const handleFetchHistoryItem = useMemoizedFn(() => {
    if (topFinished) {
      return Promise.resolve([]);
    }
    if (fetching) {
      return Promise.resolve([]);
    }

    setFetching(true);
    setTopFinished(true);

    const fetchHandle = global.subject === 'listen' ? fetchListeningAIHistory : fetchReadingAIHistory;
    // 拉取历史记录并转换成本地数据结构
    return fetchHandle({
      id: lastId,
      record_id: recordId,
    }).then((res:any) => {
      setFetching(false);
      if (res.data.list.length === 0) {
        setTopFinished(true);
        return Promise.resolve([]);
      } else {
        setLastId(res.data.list[0].id);

        let data: IAIChatItem[]; 
        // 阅读的reference后端没存，单独处理
        if (global.subject === 'read') {
          data = res.data.list.map(remoteToLocalRead);
         
        } else {
          data = res.data.list.map(remoteToLocal);
        }
        return Promise.resolve(data);
      }
    }).catch(() => {
      setTopFinished(true);
      return Promise.resolve([]);
    }).finally(() => {
      setFetching(false);
      return Promise.resolve([]);
    });
  });
  
  const handleClickReference = useMemoizedFn((id: string, reference?: IAIChatReference) => {
    // 跳转到原文文中引用
    const {number } = reference?.payload || {};
    if (number !== undefined) {
      focusNumberAI(number);
    }
    
  });

  const handleClickAnsItem = useMemoizedFn(({id, typeState, ansItem: ans, item}) => {
    if (ans.type === 'audio') {
      if (!typeState || typeState.playingChat !== id || typeState.playingItem !== ans.key) {
        // 播放当前音频
        playAudio({ start_time: ans.payload.start_time, end_time: ans.payload.end_time, number: -2 }, ans.key);
        panelRef.current?.setTypeState(ans.type, { playingChat: id, playingItem: ans.key });
      } else {
        // 停止播放当前音频
        stopAudio();
        panelRef.current?.setTypeState(ans.type, null);
      }
    } else if (ans.type === 'reference') {
      // 跳转到原文文中引用
      toReference(ans);
    }
  });

  // const handleSetAnswer = (s: string, finished: boolean) => {
  //   stopTimer();
  //   const answer = streamToAnswerItemWithMarkdown(s);
  //   panelRef.current?.appendAnswerItem(answer, finished);
  // };

  // const handleStart = use(() => {
  //   startTimer();
  //   return streamMethodGenerator(
  //     global.getWebSocketUrl(),
  //     handleSetAnswer,
  //     (error) => { },
  //     true,
  //     logger,
  //     createWebsocketInstance,
  //   );
  // }, []);
  
  const handleAsk = useMemoizedFn((s: string, item: AIChatItemModel) => {

    if (item.type === 'Analysis') {
      return; 
    }
    // 入参定义;
    let data: any; 
    if (global.subject === 'listen') {
      data = {
        type: 'Asking',
        content: s,
        record_id: Number(recordId),
        paper_id,
      };
    } else {
      data = {
        type: 'Asking',
        question: s,
        record_id: Number(recordId),
        paper_id,
      };
    }

    logBDEvent('ai-asking', { page: 'mock-test', course: global.subject === 'listen' ? 'listening' : 'reading', type: 'asking' });


    if (item.reference) {
      data.reference = {
        content: item.reference.content,
        payload: JSON.stringify(item.reference.payload),
      };
      data.question_ids = item.reference.payload?.question_ids || [];
      if (data.question_ids.length > 0) {
        data.question_id = data.question_ids[0];
      }
    }

    aiPanelAsk({
      question: s,
      item,
      url: global.getWebSocketUrl(),
      panelRef: panelRef,
      generateMessage: () => JSON.stringify(data),
      prePermission: true,
      logger,
      webSocketFactory: createWebsocketInstance,
    });


    // handleStart(JSON.stringify(data));
  });

  
  const handlePrintFinished = useMemoizedFn((data: AIChatItemModel) => {
    updatePointsDelay();
    switch (data.type) {
      case 'Analysis': {
        // 保存题目分析

        const saveHandle = global.subject === 'listen' ? saveListeningAIChatRecord: saveReadingAIChatRecord;
        saveHandle({
          type: 'Analysis',
          record_id: Number(params.recordId),
          question_id: Number(data.reference?.payload.question_id || 0),
          paper_id,
          reference: {
            content: data.reference?.content || '',
            payload: JSON.stringify(data.reference?.payload || {}),
          },
          question: data.question,
          analyze: JSON.stringify({
            question: data.question,
            answer: data.answer,
          }),
        });
        break;
      }

      default: break;
    }
  });

  const [playingAudio, setPlayingAudio] = useState<boolean>(false);
  useEffect(()=>{
    if (!playingAudio){
      panelRef.current?.setTypeState('audio', null);
    }
  }, [playingAudio]);

  const onAudioStop = useMemoizedFn(()=>{
    panelRef.current?.setTypeState('audio', null);
  });

  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.append([{
        question: '',
        answer: streamToAnswerItemWithMarkdown(i18n.t('common.dauyan_hello_i_am_your_ai_teacher')),
        id: 'normal_welcome',
        type: 'Asking',
      }]);
    }
  }, []);
  
  useEffect(() => {
    // TODO: 接收题目分析
    const getAIAnswer = (value: any) => {
      // {question_number: number; reference: IAIChatReference; answer: any[];}
      console.log(value.answer[0]);
      const {question_number, reference, answer} = value; 
      const item = AIChatItemModel.createPrintingItem({
        type: 'Analysis',
        question: question_number ? i18n.t('common.dauyan_help_me_analyze_question_order', {v: question_number}) : i18n.t('common.dauyan_help_me_analyze_this_question'),
        reference,
        answer: answer,
      }, true);
      console.log(item);
      panelRef.current?.setPrintingItem(item);
    };

    const audioStopHandle = () => {
      onAudioStop();
    };
    Bus.subscribe(AIContant.AI_PANEL_ANALYZE_QUESTION, getAIAnswer);
    // 接收停止播放音频
    Bus.subscribe(AIContant.AUDIO_STOP, audioStopHandle);
    return () => {
      Bus.unsubscribe(AIContant.AI_PANEL_ANALYZE_QUESTION, getAIAnswer);
      Bus.unsubscribe(AIContant.AUDIO_STOP, audioStopHandle);
    };
  }, []);

  const pointsInfo = useModulePoints(pointsMapp[course]);

  const AIChatInput = useMemo(() => {
    return AIChatInputHOC(pointsInfo.modulePoints);
  }, [pointsInfo]);

  return <div className={lessModule.wrapper}>
    <ChatListBackground>
      <AITitle />
      <AIPanelComp
        simple
        ref={panelRef}
        chatItems={[]}
        chatAnswerItemRenderPlugins={[
          { type: 'audio', renderer: ChatItemAudioRenderer }
        ]}
        fetchPreList={handleFetchHistoryItem}
        onClickReference={handleClickReference}
        onClickChatAnswerItem={handleClickAnsItem}
        onPrintFinished={handlePrintFinished}
        ask={handleAsk}
        inputComp={AIChatInput}
      />
    </ChatListBackground>
  </div>;
});
